import { FC, useEffect } from 'react'

import { Controller, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDebouncedCallback } from 'use-debounce'
import styled from 'styled-components'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { DEBOUNCE_DELAY } from 'src/Constants/numeric'
import { InfluencerRegisterEnum } from 'src/Types/users'
import {
  getApplicantUpdateEmailError,
  getIsConfirmationLinkSent,
} from 'src/Redux/creatorRegister-process/selectors'
import { resetCreatorError } from 'src/Redux/creatorRegister-process'
import { applicantUpdateEmail } from 'src/Redux/creatorRegister-process/api-actions'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { PrimaryInput } from 'src/Components/PrimaryInput'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import {
  creatorRegAuthorizationProceedEvent,
  creatorRegEmailConfirmationRequestedEvent,
} from 'src/Helpers/TagManager/CreatorsEvents'

import { LoginCreatorConfirmation } from '../LoginCreatorConfirmation/LoginCreatorConfirmation'
import Status from '../Status'

import { schema } from './schema'

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledCTAButton = styled(CTAButton)`
  width: fit-content;
  margin: 0 auto;
  padding-left: 40px;
  padding-right: 40px;
`

const FormTextTitle = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`
const FormText = styled(FormTextTitle)`
  font-weight: 400;
`

interface FormDataProps {
  email: string
}

interface LoginCreatorFromProps {
  status: InfluencerRegisterEnum
  setRegisterState: (value: InfluencerRegisterEnum) => void
}

const FORM_STATUSES = [
  InfluencerRegisterEnum.FORM,
  InfluencerRegisterEnum.CONFIRMATION,
]
export const LoginCreatorFrom: FC<LoginCreatorFromProps> = ({
  setRegisterState,
  status,
}) => {
  useEffect(() => {
    status === InfluencerRegisterEnum.FORM &&
      creatorRegAuthorizationProceedEvent()

    status === InfluencerRegisterEnum.CONFIRMATION &&
      creatorRegEmailConfirmationRequestedEvent()
  }, [status])

  const dispatch = useAppDispatch()
  const { search } = useCustomSearchParams()
  const authToken = search.get('authToken') || ''
  const authError = useAppSelector(getApplicantUpdateEmailError)
  const isConfirmationLinkSent = useAppSelector(getIsConfirmationLinkSent)

  const {
    register,
    control,
    handleSubmit,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<FormDataProps>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    resolver: yupResolver(schema),
  })

  const watchEmail = useWatch({ control, name: 'email', defaultValue: '' })

  const handleClearErrors = (): void => {
    clearErrors(['email'])
    dispatch(resetCreatorError())
  }

  const debouncedApplicantUpdateEmailHandler = useDebouncedCallback(() => {
    const body = {
      email: watchEmail,
      authToken,
    }
    void dispatch(applicantUpdateEmail(body))
      .unwrap()
      .then((res) => {
        setRegisterState(InfluencerRegisterEnum.CONFIRMATION)
        return res
      })
      .catch(() => null)
  }, DEBOUNCE_DELAY)

  if (!FORM_STATUSES.includes(status)) {
    return null
  }

  if (status === InfluencerRegisterEnum.CONFIRMATION) {
    return (
      <LoginCreatorConfirmation
        email={watchEmail}
        isSending={isConfirmationLinkSent}
        onClickSendAgain={debouncedApplicantUpdateEmailHandler}
        onClickChangeEmail={() => setRegisterState(InfluencerRegisterEnum.FORM)}
      />
    )
  }

  const serverError = authError ? 'Email is already exist' : ''

  return (
    <>
      <Status step={2} />
      <Form onSubmit={handleSubmit(debouncedApplicantUpdateEmailHandler)}>
        <div>
          <FormTextTitle>Set your contact email</FormTextTitle>
          <FormText>
            We’ll use this email to send your payouts - please enter one you
            actively use.
          </FormText>
        </div>
        <Controller
          name='email'
          control={control}
          defaultValue=''
          render={({ field: { onChange } }) => (
            <PrimaryInput
              {...register('email')}
              name='email'
              type='email'
              autoComplete='off'
              error={errors?.email?.message || serverError}
              placeholder='Enter your email'
              onChange={onChange}
              onFocus={handleClearErrors}
              onTouchStart={handleClearErrors}
            />
          )}
        />
      </Form>
      <StyledCTAButton
        onClick={handleSubmit(debouncedApplicantUpdateEmailHandler)}
        disabled={!isValid || authError || isConfirmationLinkSent}
      >
        Send confirmation link
      </StyledCTAButton>
    </>
  )
}
